import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import TargetMenuModal from 'components/TargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError, TARGET_MENU_WIZARD } from 'utils';
import { WizardDetailModal } from '../components/WizardDetailModal';
import { WizardListResponse } from '../models/wizard';
import targetMenuWizardService from '../targetMenuWizardService';
import wizardService from '../wizardService';

function WizardListPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const btnViewDetailRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);
  const arrChkSelectRef = useRef<Array<HTMLInputElement>>([]);
  const selectedWizardRef = useRef<WizardListResponse | undefined>(undefined);

  //State
  const [wizardList, setWizardList] = useState<Array<WizardListResponse>>([]);
  const [isOpenTargetMenuModal, setIsOpenTargetMenuModal] = useState(false);
  const [showWizardDetailModal, setShowWizardDetailModal] = useState(false);

  // side effects
  useEffect(() => {
    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;

    loadDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // events
  async function loadDataList() {
    try {
      const response = await wizardService.getAll();
      setWizardList(response.data);
      handleUnSelectAll();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleUnSelectAll() {
    // reset checkbox selection
    arrChkSelectRef.current.forEach((elem) => {
      if (elem) {
        elem.checked = false;
      }
    });
    arrChkSelectRef.current = [];

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
    selectedWizardRef.current = undefined;
  }

  function getSelectedElements() {
    return arrChkSelectRef.current.filter((elem) => elem && elem.checked);
  }

  function handleSelectWizard() {
    const checkedElements = getSelectedElements();

    if (btnViewDetailRef.current && btnDeleteRef.current) {
      if (checkedElements.length === 0) {
        btnViewDetailRef.current.disabled = true;
        btnDeleteRef.current.disabled = true;
      } else if (checkedElements.length === 1) {
        btnViewDetailRef.current.disabled = false;
        btnDeleteRef.current.disabled = false;
      } else {
        btnViewDetailRef.current.disabled = true;
        btnDeleteRef.current.disabled = false;
      }
    }
  }

  function handleOpenWizardDetailModal() {
    handleUnSelectAll();
    setShowWizardDetailModal(true);
  }

  function handleShowDetail() {
    const checkedElements = getSelectedElements();
    if (checkedElements.length !== 1) {
      return;
    }

    const selectedId = Number(checkedElements[0].value);
    selectedWizardRef.current = wizardList.find((wizard) => wizard.id === selectedId);
    setShowWizardDetailModal(true);
  }

  function handleCloseWizardDetailModal(isChanged: boolean) {
    setShowWizardDetailModal(false);
    handleUnSelectAll();

    if (isChanged) {
      loadDataList();
    }
  }

  async function processDelete(ids: number[]) {
    try {
      const response = await wizardService.delete({ ids });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadDataList();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function checkDeleteValidation() {
    try {
      const checkedElements = getSelectedElements();
      const selectedIds = checkedElements.map((elem) => Number(elem.value));

      const response = await wizardService.checkDeleteValidation({ ids: selectedIds });
      return response.data;
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function handleDelete() {
    const checkedElements = getSelectedElements();
    if (checkedElements.length === 0) {
      return;
    }

    const checkResults = await checkDeleteValidation();
    if (checkResults && checkResults.length > 0) {
      const invalidDeleteList = checkResults.filter((item) => item.menu_count > 0);
      const validDeleteIds = checkResults.filter((item) => item.menu_count === 0).map((item) => item.id);

      if (invalidDeleteList.length > 0) {
        if (validDeleteIds.length === 0) {
          // only one valid delete wizard
          showGlobalModal(() => (
            <AlertModal
              title={t('App.confirm')}
              message={t('WizardListPage.warn_alert_delete_with_invalid_wizard', {
                invalid_wizard_names: invalidDeleteList.map((item) => item.name).join(', '),
              })}
            />
          ));
          return;
        } else {
          // multiple valid delete wizards
          const invalidWizardNames = invalidDeleteList.map((item) => item.name).join(', ');
          showGlobalModal(() => (
            <ConfirmModal
              title={t('App.confirm')}
              message={t('WizardListPage.warn_confirm_delete_with_invalid_wizard', {
                invalid_wizard_names: invalidWizardNames,
              })}
              onConfirm={processDelete.bind(null, validDeleteIds)}
            />
          ));
          return;
        }
      }

      // have no invalid delete wizard
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('WizardListPage.warn_confirm_delete')}
          onConfirm={processDelete.bind(null, validDeleteIds)}
        />
      ));
    }
  }

  async function processRestore(id: number) {
    try {
      const response = await wizardService.restore({ ids: [id] });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadDataList();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleRestore(id: number) {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('WizardListPage.warn_confirm_restore')}
        onConfirm={processRestore.bind(null, id)}
      />
    ));
  }

  // target menu wizard handlers
  function handleLoadMenuWizard(wizard_id: number) {
    return targetMenuWizardService.getTargetMenuWizardList({ wizard_id: wizard_id });
  }

  function handleAddMenuWizard(wizardId: number, menuIds: number[]) {
    // compose payload
    const payload: { wizard_id: number; menu_ids: number[] } = {
      wizard_id: wizardId,
      menu_ids: menuIds,
    };
    return targetMenuWizardService.addTargetMenuWizard(payload);
  }

  function handleDeleteMenuWizard(wizardId: number, menuIds: number[]) {
    // compose payload
    const payload: { wizard_id: number; menu_ids: number[] } = {
      wizard_id: wizardId,
      menu_ids: menuIds,
    };
    return targetMenuWizardService.deleteTargetMenuWizard(payload);
  }

  function handleShowMenuWizard(wizard: WizardListResponse) {
    selectedWizardRef.current = wizard;
    setIsOpenTargetMenuModal(true);
  }

  function handleCloseMenuWizard() {
    handleUnSelectAll();
    setIsOpenTargetMenuModal(false);
  }
  // end target menu wizard handlers

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div className="mb-3">
            <FeatureTitleBar title={t('WizardListPage.head_title')}></FeatureTitleBar>
          </div>
          {/* actions */}
          <div className="text-end mb-2">
            <Button className="btn--dark-green" onClick={handleOpenWizardDetailModal}>
              {t('WizardListPage.add_new')}
            </Button>
            <Button ref={btnViewDetailRef} className="ms-3" onClick={handleShowDetail}>
              {t('WizardListPage.detailed')}
            </Button>
            <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
              {t('WizardListPage.delete')}
            </Button>
          </div>
          {/* list data */}
          <div className="sticky-table flex-grow-1 flex-shrink-1">
            <Table hover>
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>{t('WizardListPage.selection')}</th>
                  <th style={{ width: '24%' }}>{t('WizardListPage.wizard_name')}</th>
                  <th style={{ width: '60%' }}>{t('WizardListPage.note')}</th>
                  <th style={{ width: '12%' }}>{t('WizardListPage.target_menu')}</th>
                </tr>
              </thead>
              <tbody>
                {wizardList.map((wizard, index) => {
                  return (
                    <tr key={wizard.id} className={`${wizard.delete_flg ? 'bg-light' : ''}`}>
                      <td className="text-center">
                        {wizard.delete_flg && (
                          <Button className="btn--orange" onClick={handleRestore.bind(null, wizard.id)}>
                            {t('WizardListPage.restore')}
                          </Button>
                        )}
                        {!wizard.delete_flg && (
                          <Form.Check
                            ref={(el: HTMLInputElement) => {
                              arrChkSelectRef.current[index] = el;
                            }}
                            name="selected_wizard"
                            className="custom-checkbox"
                            value={wizard.id}
                            onChange={handleSelectWizard}
                          ></Form.Check>
                        )}
                      </td>
                      <td className="text-center text-wrap">{wizard.name}</td>
                      <td className="text-wrap">{wizard.note}</td>
                      <td className="text-center">
                        {!wizard.delete_flg && (
                          <Button
                            className="flex-grow-0 flex-shrink-0"
                            onClick={handleShowMenuWizard.bind(null, wizard)}
                          >
                            {t('WizardListPage.target_menu')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {selectedWizardRef.current &&
        isOpenTargetMenuModal &&
        createPortal(
          <TargetMenuModal
            targetSourceType={TARGET_MENU_WIZARD}
            targetName={selectedWizardRef.current.name}
            onLoadData={handleLoadMenuWizard.bind(null, selectedWizardRef.current!.id)}
            onAdd={(menuIds: number[]) => handleAddMenuWizard(selectedWizardRef.current!.id, menuIds)}
            onDelete={(menuIds: number[]) => handleDeleteMenuWizard(selectedWizardRef.current!.id, menuIds)}
            onClose={handleCloseMenuWizard}
          />,
          document.body
        )}

      {showWizardDetailModal &&
        createPortal(
          <WizardDetailModal wizardId={selectedWizardRef.current?.id} onClose={handleCloseWizardDetailModal} />,
          document.body
        )}
    </div>
  );
}

export default WizardListPage;
